import { useQuery } from "@tanstack/react-query";

import { apiContent } from "@/services/api-client/content";
import { RunStatus, type UseCaseRun } from "@/types/content";

import { contentKeys } from "./content.keys";

const POLL_INTERVAL = 10_000;
const ACTIVE_STATUSES = [RunStatus.ACCEPTED, RunStatus.RUNNING];

const isActiveRun = (run: UseCaseRun) =>
  ACTIVE_STATUSES.includes(run.status as RunStatus);

export const useGetTaskTypesQuery = () =>
  useQuery({
    queryKey: contentKeys.taskTypes(),
    queryFn: () => apiContent.getTaskTypes(),
    // Keep task types fresh for 5 min to reflect recent task-type changes
    // without excessive refetching (task-types are updated rarely)
    staleTime: 5 * 60_000,
  });

export const useGetRunsQuery = (page: number, pageSize: number) =>
  useQuery({
    queryKey: [...contentKeys.runsList(pageSize), page],
    queryFn: () => apiContent.getRuns(pageSize, (page - 1) * pageSize),
    placeholderData: (prev) => prev,
    refetchInterval: (query) =>
      query.state.data?.items.some(isActiveRun) ? POLL_INTERVAL : false,
  });

export const useGetRunQuery = (runId: string | undefined) =>
  useQuery({
    queryKey: contentKeys.run(runId ?? ""),
    queryFn: () => apiContent.getRun(runId as string),
    enabled: !!runId,
    refetchInterval: (query) =>
      query.state.data && isActiveRun(query.state.data) ? POLL_INTERVAL : false,
  });
